import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Calendar,
  Check,
  Menu,
  FolderOpen,
  Receipt,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import founders from '../assets/founders.jpg';
import { PRICE_RANGE_TEXT } from '../constants/pricing';

const NAV_LINKS = [
  { href: '#features', label: 'Funktioner' },
  { href: '#how', label: 'Så funkar det' },
  { href: '#story', label: 'Om oss' },
  { href: '#pricing', label: 'Priser' },
  { href: '#faq', label: 'Vanliga frågor' },
];

const FEATURES = [
  {
    icon: Users,
    title: 'Medlemsregister',
    description: 'Håll koll på alla fastigheter, medlemmar och kontaktuppgifter — importera er lista och exportera när ni vill.',
  },
  {
    icon: Receipt,
    title: 'Fakturering',
    description: 'Skicka fakturor och låt betalpåminnelserna gå ut automatiskt. Se direkt vem som betalat och vem som väntar.',
  },
  {
    icon: Calendar,
    title: 'Årsmöten',
    description: 'Skicka kallelser med ett klick. När protokollet är justerat skickas det automatiskt till alla medlemmar.',
  },
  {
    icon: FolderOpen,
    title: 'Dokumenthantering',
    description: 'Stadgar, protokoll och avtal samlade och lätta att hitta — för hela styrelsen, år efter år.',
  },
];

const HOW_IT_WORKS = [
  {
    title: 'Skapa er förening',
    description: 'Registrera föreningen och bjud in resten av styrelsen på ett par minuter.',
  },
  {
    title: 'Lägg in medlemmar',
    description: 'Importera eller lägg till medlemmar och fastigheter — klart för fakturering direkt.',
  },
  {
    title: 'Låt föreningen skötas',
    description: 'Betalpåminnelser och justerade protokoll går ut automatiskt från dag ett.',
  },
];

const PRICING_INCLUDES = [
  'Obegränsat antal medlemmar och fastigheter',
  'Fakturering och automatiska betalpåminnelser',
  'Digitala årsmöteskallelser och dokumentarkiv',
  'Support på svenska',
];

const FAQS = [
  {
    id: 'kostnad',
    question: 'Kostar det något att komma igång?',
    answer:
      `Nej. Första året är helt kostnadsfritt och utan bindningstid. Efter det kostar det ${PRICE_RANGE_TEXT} beroende på hur stor föreningen är.`,
  },
  {
    id: 'flytt',
    question: 'Hur lång tid tar det att flytta över vår förening?',
    answer: 'De flesta styrelser är igång samma dag. Ni kan importera medlemslistor direkt eller lägga in dem manuellt i lugn takt.',
  },
  {
    id: 'medlemmar',
    question: 'Kan medlemmarna själva se sina betalningar?',
    answer: 'Ja, varje medlem kan logga in och se sin egen betalningshistorik, aviseringar och föreningens dokument.',
  },
  {
    id: 'avsluta',
    question: 'Vad händer om vi vill avsluta?',
    answer: 'Inga uppsägningstider eller avtal. Ni kan avsluta när ni vill och exportera er data.',
  },
  {
    id: 'integritetspolicy',
    question: 'Hur hanterar ni våra medlemmars uppgifter?',
    answer:
      'Vi samlar bara in de uppgifter som behövs för att sköta föreningen — inget mer. Data lagras säkert inom EU, delas aldrig med tredje part för marknadsföring och kan raderas på begäran.',
  },
];

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

const SOCIALS = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/share/1Lgf9d7nVT/',
    icon: FacebookIcon,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/vagforening.se/',
    icon: InstagramIcon,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/v%C3%A4gf%C3%B6rening-se/',
    icon: LinkedinIcon,
  },
];

function SocialLinks() {
  return (
    <div className="mt-5">
      <h4 className="mb-3 text-xs font-bold text-teal-900">Följ oss</h4>
      <ul className="flex items-center gap-2.5">
        {SOCIALS.map(({ name, href, icon: Icon }) => (
          <li key={name}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={name}
              title={name}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-teal-100 bg-teal-50 text-teal-600 transition hover:-translate-y-0.5 hover:border-teal-200 hover:bg-teal-100 hover:text-teal-800 hover:shadow-md"
            >
              <Icon className="h-5 w-5" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrandMark({ onClick }: { onClick?: () => void }) {
  return (
    <Link
      to="/"
      onClick={() => {
        onClick?.();
        // Deferred so it runs after the mobile menu has unlocked page scrolling.
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
      }}
      aria-label="Vägförening.se – till startsidan"
      className="flex items-center gap-2 font-bold text-teal-900"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-600 to-teal-800 shadow-sm">
        <Building2 className="h-5 w-5 text-white" />
      </span>
      Vägförening.se
    </Link>
  );
}

function openFaq(id: string) {
  const item = document.getElementById(id);
  if (item instanceof HTMLDetailsElement) {
    item.open = true;
    item.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function PrimaryButton({
  to,
  children,
  className = '',
  onClick,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-teal-900/20 transition hover:shadow-xl ${className}`}
    >
      {children}
    </Link>
  );
}

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    // The menu is phone-only; close it if the viewport grows to the desktop nav.
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onBreakpoint = () => desktop.matches && setMenuOpen(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    desktop.addEventListener('change', onBreakpoint);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', onBreakpoint);
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b border-teal-100 backdrop-blur ${menuOpen ? 'bg-white' : 'bg-white/90'}`}
      >
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-2 px-4 sm:px-6">
          <BrandMark onClick={closeMenu} />
          <nav aria-label="Huvudmeny" className="hidden items-center gap-8 text-sm font-medium text-slate-600 lg:flex">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-teal-700">{link.label}</a>
            ))}
          </nav>
          <div className="hidden items-center gap-5 lg:flex">
            <Link to="/login" className="text-sm font-semibold text-teal-800 hover:text-teal-900">
              Logga in
            </Link>
            <PrimaryButton to="/register" className="px-4 py-2 text-sm">
              Kom igång gratis
            </PrimaryButton>
          </div>
          {/* Most visitors are on phones, so sign-up stays visible; login lives in the menu. */}
          <div className="-mr-2 flex items-center gap-1 lg:hidden">
            <PrimaryButton to="/register" onClick={closeMenu} className="whitespace-nowrap px-3 py-2 text-sm">
              Kom igång<span className="hidden min-[380px]:inline"> gratis</span>
            </PrimaryButton>
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobilmeny"
              aria-label={menuOpen ? 'Stäng meny' : 'Öppna meny'}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-teal-900 hover:bg-teal-50"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobilmeny"
            aria-label="Mobilmeny"
            className="absolute inset-x-0 top-full max-h-[calc(100dvh-72px)] overflow-y-auto border-b border-teal-100 bg-white px-4 pb-6 pt-2 shadow-xl sm:px-6 lg:hidden"
          >
            <ul className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={closeMenu}
                    className="flex items-center justify-between border-b border-slate-100 py-4 text-base font-semibold text-teal-900"
                  >
                    {link.label}
                    <ArrowRight className="h-4 w-4 text-teal-500" />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-col gap-3">
              <PrimaryButton to="/register" onClick={closeMenu} className="w-full py-3.5 text-base">
                Kom igång gratis
              </PrimaryButton>
              <Link
                to="/login"
                onClick={closeMenu}
                className="inline-flex w-full items-center justify-center rounded-lg border border-slate-200 px-6 py-3.5 text-base font-semibold text-teal-800 hover:bg-teal-50"
              >
                Logga in
              </Link>
            </div>
            <p className="mt-4 text-center text-sm text-slate-500">Gratis första året · Ingen bindningstid</p>
          </nav>
        )}
      </header>
      {menuOpen && (
        // Outside the header: its backdrop-blur would make this fixed overlay header-sized.
        <div aria-hidden="true" onClick={closeMenu} className="fixed inset-0 z-40 bg-slate-900/30 lg:hidden" />
      )}
    </>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SiteHeader />

      <section className="bg-gradient-to-b from-teal-50 via-white to-white px-6 py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
          <div>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-teal-900 sm:text-5xl">
              Allt din vägförening behöver — <span className="text-teal-500">på ett ställe</span>
            </h1>
            <p className="mt-5 max-w-lg text-lg text-slate-600">
              Medlemsregister, fakturering, årsmöten och dokument samlat i ett verktyg — så att styrelsen kan lägga
              tiden på föreningen, inte på Excel-ark och mejltrådar.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <PrimaryButton to="/register">Kom igång gratis</PrimaryButton>
              <a
                href="#how"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-6 py-3 text-sm font-semibold text-teal-800 transition hover:border-teal-300 hover:bg-teal-50"
              >
                Se hur det funkar
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-4 text-sm text-slate-500">Gratis första året · Inget kreditkort krävs · Ingen bindningstid</p>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-2xl">
              <div className="flex items-center gap-2 border-b border-teal-100 bg-teal-50 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-teal-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-teal-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-teal-200" />
                <span className="ml-1 text-xs text-slate-500">vagforening.se/dashboard</span>
              </div>
              <div className="p-5">
                <div className="mb-3 flex items-center justify-between text-sm font-bold text-teal-900">
                  Medlemsöversikt
                  <span className="rounded-md bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-600">24 medlemmar</span>
                </div>
                {[
                  { name: 'Anna Karlsson', status: 'ok' },
                  { name: 'Erik Svensson', status: 'ok' },
                  { name: 'Maria Lindqvist', status: 'wait' },
                  { name: 'Johan Bergström', status: 'ok' },
                ].map((member) => (
                  <div key={member.name} className="flex items-center justify-between border-b border-teal-50 py-3 text-sm last:border-none">
                    <div className="flex items-center gap-2.5">
                      <span className="h-6 w-6 rounded-full border border-teal-100 bg-teal-50" />
                      {member.name}
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        member.status === 'ok' ? 'bg-teal-50 text-teal-600' : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {member.status === 'ok' ? 'Betald' : 'Väntar'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute -right-6 -top-5 hidden w-44 rounded-2xl border border-teal-100 bg-white p-4 shadow-xl sm:block">
              <div className="text-xs font-bold text-teal-900">Inbetalt i år</div>
              <div className="text-xl font-extrabold text-teal-700">96%</div>
            </div>

            <div className="absolute -bottom-6 -left-6 hidden w-52 rounded-2xl border border-teal-100 bg-white p-4 shadow-xl sm:block">
              <div className="text-xs font-bold text-teal-900">Årsmöte 2026</div>
              <div className="mt-1 text-xs text-slate-500">Kallelse skickad till 24 medlemmar</div>
              <div className="mt-2 inline-block rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-bold text-teal-600">
                Skickat automatiskt
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="scroll-mt-20 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-xl text-center">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-teal-500">Funktioner</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-teal-900 sm:text-4xl">Ett verktyg istället för fem</h2>
            <p className="mt-3 text-slate-600">Sluta jonglera Excel, mejl och lösa lappar. Allt en styrelse behöver finns redan här.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-6 transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 font-semibold text-teal-900">{title}</h3>
                <p className="text-sm text-slate-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="scroll-mt-20 px-6 pb-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-xl text-center">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-teal-500">Så funkar det</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-teal-900 sm:text-4xl">Igång på under 10 minuter</h2>
            <p className="mt-3 text-slate-600">Ingen installation, ingen IT-avdelning. Skapa konto och börja lägga in medlemmar direkt.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {HOW_IT_WORKS.map((step, index) => (
              <div key={step.title} className="flex gap-4">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-teal-200 text-xs font-semibold text-teal-600">
                  {index + 1}
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-slate-700">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 to-teal-900 px-8 py-12 text-center shadow-xl shadow-teal-900/20 sm:px-12">
            <h3 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Se hur det fungerar – direkt</h3>
            <p className="mx-auto mt-3 max-w-xl text-teal-100">
              Klicka runt i en påhittad vägförening med medlemmar, möten och fakturor. En guide visar dig steg för steg.
            </p>
            <Link
              to="/demo"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-8 py-4 text-base font-bold text-slate-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-xl"
            >
              Prova testföreningen <ArrowRight className="h-5 w-5" />
            </Link>
            <p className="mt-4 text-sm text-teal-200">Ingen registrering – inget sparas.</p>
          </div>
        </div>
      </section>

      <section id="story" className="scroll-mt-20 px-6 pb-24">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl border border-slate-200 shadow-lg lg:grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative flex min-h-[420px] flex-col justify-end gap-4 overflow-hidden p-10 text-white">
            <img
              src={founders}
              alt="Grundarna av Vägförening.se"
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-950/90 via-teal-900/50 to-teal-900/10" />
            <div className="relative text-5xl font-extrabold text-white/40">&ldquo;</div>
            <p className="relative text-lg leading-relaxed">
              Vi satt själva i styrelsen och kämpade med Excel-ark, mejltrådar och sena betalningar. Så vi byggde
              lösningen vi själva önskat att vi hade haft.
            </p>
            <div className="relative mt-2">
              <div className="text-sm font-semibold">Grundarna av Vägförening.se</div>
              <div className="text-xs text-white/70">Tidigare styrelseledamöter</div>
            </div>
          </div>
          <div className="bg-white p-10">
            <h3 className="mb-3 text-xl font-bold text-teal-900">Byggd av dem som förstår problemet</h3>
            <p className="mb-3 text-slate-600">
              Idén föddes ur egen erfarenhet. Grundarna har själva suttit i styrelsen för sin egen vägförening och
              upplevde svårigheterna med att hålla ordning på medlemmar, betalningar och dokument manuellt.
            </p>
            <p className="mb-4 text-slate-600">
              Resultatet är ett verktyg byggt av folk som faktiskt vet hur en vägförenings vardag ser ut — inte en
              generisk föreningsapp.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Byggd i Sverige', 'Av styrelsemedlemmar', 'För vägföreningar'].map((tag) => (
                <span key={tag} className="rounded-full bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="testimonial" className="scroll-mt-20 px-6 pb-24">
        <div className="mx-auto max-w-3xl rounded-3xl border border-teal-100 bg-teal-50/60 p-10 text-center sm:p-12">
          <div className="mx-auto mb-4 text-5xl font-extrabold text-teal-300">&ldquo;</div>
          <p className="text-lg font-medium leading-relaxed text-teal-950 sm:text-xl">
            Vägförening.se har gjort det enklare för oss att hålla ihop föreningen och kommunikationen med våra
            medlemmar. Det är enkelt, tydligt och precis vad vi behöver.
          </p>
          <div className="mt-6 text-sm font-semibold text-teal-700">— Högarydsvägen samfällighetsförening</div>
        </div>
      </section>

      <section id="pricing" className="scroll-mt-20 px-6 pb-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-xl text-center">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-teal-500">Priser</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-teal-900 sm:text-4xl">Enkel prissättning, inga överraskningar</h2>
          </div>
          <div className="mx-auto grid max-w-3xl gap-8 rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-teal-50 p-8 shadow-lg sm:p-11 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <h3 className="mb-3 text-lg font-bold text-teal-900">Allt ingår, från start</h3>
              <ul className="space-y-2.5">
                {PRICING_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-slate-200 pt-6 text-center lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
              <span className="mb-3 inline-block rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-600">
                Första året
              </span>
              <div className="text-4xl font-extrabold tracking-tight text-teal-900">0 kr</div>
              <p className="mx-auto mt-2.5 mb-5 max-w-[220px] text-xs text-slate-500">
                Ingen bindningstid, inget kreditkort. Efter första året <strong>{PRICE_RANGE_TEXT}</strong> beroende på
                föreningens storlek.
              </p>
              <PrimaryButton to="/register" className="w-full">
                Kom igång gratis
              </PrimaryButton>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="scroll-mt-20 px-6 pb-24">
        <div className="mx-auto max-w-3xl">
          <div className="mb-12 text-center">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-teal-500">Vanliga frågor</div>
            <h2 className="text-3xl font-extrabold tracking-tight text-teal-900 sm:text-4xl">Bra att veta innan ni börjar</h2>
          </div>
          <div className="flex flex-col gap-3">
            {FAQS.map((faq, index) => (
              <details key={faq.id} id={faq.id} className="group scroll-mt-24 rounded-2xl border border-slate-200 bg-white" open={index === 0}>
                <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 font-semibold text-teal-900">
                  {faq.question}
                  <span className="text-xl font-normal text-teal-500 transition group-open:rotate-45">+</span>
                </summary>
                <div className="px-5 pb-4 text-sm text-slate-600">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 to-teal-900 px-8 py-16 text-center text-white shadow-2xl">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Redo att lämna Excel-arken bakom er?</h2>
          <p className="mx-auto mt-3 mb-7 max-w-md text-white/80">
            Kom igång på under 10 minuter — gratis första året, ingen bindningstid.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-teal-800 shadow-lg transition hover:shadow-xl"
            >
              Kom igång gratis
            </Link>
            <a
              href="#faq"
              className="inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              Vanliga frågor
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 px-6 py-14">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <BrandMark />
              <p className="mt-3.5 max-w-[280px] text-sm text-slate-600">
                Allt din vägförening behöver — på ett ställe. Byggt i Sverige, av styrelsemedlemmar, för
                styrelsemedlemmar.
              </p>
              <SocialLinks />
            </div>
            <div>
              <h4 className="mb-3.5 text-xs font-bold text-teal-900">Produkt</h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600">
                <li><a href="#features" className="hover:text-teal-700">Funktioner</a></li>
                <li><a href="#pricing" className="hover:text-teal-700">Priser</a></li>
                <li><a href="#faq" className="hover:text-teal-700">Vanliga frågor</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3.5 text-xs font-bold text-teal-900">Företag</h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600">
                <li><a href="#story" className="hover:text-teal-700">Om oss</a></li>
                <li><a href="mailto:noreply@vägförening.se" className="hover:text-teal-700">Kontakt</a></li>
                <li>
                  <a
                    href="#integritetspolicy"
                    onClick={e => {
                      e.preventDefault();
                      openFaq('integritetspolicy');
                    }}
                    className="inline-flex items-center gap-1.5 hover:text-teal-700"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Integritetspolicy
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3.5 text-xs font-bold text-teal-900">Kontakt</h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600">
                <li><a href="mailto:noreply@vägförening.se" className="hover:text-teal-700">noreply@vägförening.se</a></li>
                <li><Link to="/unsubscribe" className="hover:text-teal-700">Avsluta sms/e-postutskick</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-2.5 border-t border-slate-200 pt-6 text-sm text-slate-500">
            <span>© 2026 Vägförening.se. Alla rättigheter förbehållna.</span>
            <span>Byggt med omtanke för svenska vägföreningar 🇸🇪</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
